import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Paper,
  Divider,
  Avatar,
  Alert,
  useTheme,
  Link,
} from "@mui/material";
import {
  EventNote as EventNoteIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationOnIcon,
  Person as PersonIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { axiosInstance } from "../../utils/axios";
import { styled } from "@mui/material/styles";

// Custom styled components
const HeaderSection = styled(Box)(({ theme }) => ({
  position: "relative",
  height: "50vh",
  minHeight: "300px",
  color: "#fff",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  overflow: "hidden",
  backgroundSize: "cover",
  backgroundPosition: "center",
  boxShadow: theme.shadows[3],
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 1,
  },
}));

const HeaderContent = styled(Box)(({ theme }) => ({
  position: "relative",
  zIndex: 2,
  padding: theme.spacing(4),
  textAlign: "center",
}));

const SectionContainer = styled(Container)(({ theme }) => ({
  marginTop: theme.spacing(6),
  marginBottom: theme.spacing(6),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  position: "relative",
  display: "inline-block",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: -10,
    left: 0,
    width: "50px",
    height: "4px",
    backgroundColor: theme.palette.primary.main,
  },
}));

const SpeakerCard = styled(Card)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: theme.shadows[8],
  },
}));

const AgendaItem = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
  transition: "transform 0.2s ease",
  "&:hover": {
    transform: "scale(1.01)",
  },
}));

const SponsorLogo = styled(Box)(({ theme }) => ({
  height: 100,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(2),
}));

const StandaloneLandingPagePreview = () => {
  const { publicUrl } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [eventData, setEventData] = useState(null);
  const [sectionsData, setSectionsData] = useState(null);
  const [error, setError] = useState(null);
  const theme = useTheme();

  useEffect(() => {
    fetchLandingPageData();
  }, [publicUrl]);

  const fetchLandingPageData = async () => {
    try {
      setLoading(true);
      console.log("Fetching event data for publicUrl:", publicUrl);

      // Fetch event and landing page data
      const response = await axiosInstance.post("/graphql", {
        query: `
          query GetEventByPublicUrl($publicUrl: String!) {
            getEventByPublicUrl(publicUrl: $publicUrl) {
              id
              name
              startDate
              endDate
              startTime
              endTime
              timezone
              organizedBy
              about
              landingPage {
                sections
              }
            }
          }
        `,
        variables: {
          publicUrl,
        },
      });

      if (response.errors) {
        throw new Error(
          response.errors[0]?.message || "Error fetching event data"
        );
      }

      const event = response.data?.getEventByPublicUrl;

      if (!event) {
        throw new Error("Event not found");
      }

      setEventData(event);

      // Parse sections from the landing page data
      if (event.landingPage?.sections) {
        try {
          const parsedData = JSON.parse(event.landingPage.sections);
          console.log("Successfully parsed landing page sections:", parsedData);
          setSectionsData(parsedData);
        } catch (error) {
          console.error("Error parsing sections data:", error);
          setError("Failed to parse landing page content");
        }
      } else {
        console.warn("No landing page sections found");
        setError("No landing page content available");
      }
    } catch (error) {
      console.error("Error fetching landing page data:", error);
      setError(error.message || "Failed to load event data");
    } finally {
      setLoading(false);
    }
  };

  // Header section renderer
  const renderHeader = () => {
    if (!sectionsData || !sectionsData.header) return null;

    const { header } = sectionsData;
    const headerStyle = header.backgroundImage
      ? { backgroundImage: `url(${header.backgroundImage})` }
      : {
          backgroundColor: header.backgroundColor || theme.palette.primary.dark,
        };

    const textColor = header.textColor || "#ffffff";

    return (
      <HeaderSection style={headerStyle}>
        <HeaderContent>
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            sx={{ color: textColor }}
          >
            {header.title || eventData?.name || "Event Title"}
          </Typography>
          <Typography variant="h5" paragraph sx={{ color: textColor }}>
            {header.subtitle || "Join us for this exciting event!"}
          </Typography>
          {header.showButton && (
            <Button
              variant="contained"
              size="large"
              sx={{
                mt: 2,
                backgroundColor:
                  header.buttonColor || theme.palette.secondary.main,
                "&:hover": {
                  backgroundColor: header.buttonColor
                    ? `${header.buttonColor}dd`
                    : theme.palette.secondary.dark,
                },
                color: "#ffffff",
              }}
            >
              {header.buttonText || "Register Now"}
            </Button>
          )}
        </HeaderContent>
      </HeaderSection>
    );
  };

  // About section renderer
  const renderAbout = () => {
    if (!sectionsData || !sectionsData.about) return null;

    const { about } = sectionsData;
    const backgroundColor = about.backgroundColor || "#ffffff";
    const titleColor = about.titleColor || "#212121";
    const textColor = about.textColor || "#424242";

    return (
      <Box sx={{ backgroundColor }}>
        <SectionContainer>
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography variant="h3" component="h2" sx={{ color: titleColor }}>
              {about.title || "About The Event"}
            </Typography>
            <Divider
              sx={{
                width: 50,
                mx: "auto",
                my: 2,
                borderColor: theme.palette.primary.main,
                borderWidth: 3,
              }}
            />
          </Box>

          <Box sx={{ maxWidth: 800, mx: "auto" }}>
            <Typography
              variant="body1"
              sx={{ color: textColor }}
              dangerouslySetInnerHTML={{
                __html: about.description || eventData?.about || "",
              }}
            />
          </Box>
        </SectionContainer>
      </Box>
    );
  };

  // Speakers section renderer
  const renderSpeakers = () => {
    if (
      !sectionsData ||
      !sectionsData.speakers ||
      !sectionsData.speakers.items ||
      sectionsData.speakers.items.length === 0
    ) {
      return null;
    }

    const { speakers } = sectionsData;
    const backgroundColor = speakers.backgroundColor || "#f5f5f5";
    const titleColor = speakers.titleColor || "#212121";
    const textColor = speakers.textColor || "#424242";

    return (
      <Box sx={{ backgroundColor }}>
        <SectionContainer>
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography variant="h3" component="h2" sx={{ color: titleColor }}>
              {speakers.title || "Speakers"}
            </Typography>
            <Divider
              sx={{
                width: 50,
                mx: "auto",
                my: 2,
                borderColor: theme.palette.primary.main,
                borderWidth: 3,
              }}
            />

            {speakers.subtitle && (
              <Typography variant="h6" sx={{ color: textColor, mt: 2 }}>
                {speakers.subtitle}
              </Typography>
            )}
          </Box>

          <Grid container spacing={4} justifyContent="center">
            {speakers.items.map((speaker, index) => (
              <Grid item xs={12} sm={6} md={4} key={speaker.id || index}>
                <SpeakerCard elevation={3}>
                  <Box
                    sx={{
                      height: 220,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "#e0e0e0",
                    }}
                  >
                    {speaker.image ? (
                      <CardMedia
                        component="img"
                        height="220"
                        image={speaker.image}
                        alt={speaker.name}
                      />
                    ) : (
                      <Avatar sx={{ width: 120, height: 120 }}>
                        <PersonIcon sx={{ fontSize: 80 }} />
                      </Avatar>
                    )}
                  </Box>
                  <CardContent sx={{ textAlign: "center" }}>
                    <Typography
                      variant="h6"
                      component="div"
                      sx={{ color: titleColor }}
                    >
                      {speaker.name}
                    </Typography>
                    {speaker.title && (
                      <Typography
                        variant="body1"
                        sx={{ color: textColor, fontWeight: 500, mt: 1 }}
                      >
                        {speaker.title}
                      </Typography>
                    )}
                    {speaker.organization && (
                      <Typography
                        variant="body2"
                        sx={{ color: textColor, mt: 0.5 }}
                      >
                        {speaker.organization}
                      </Typography>
                    )}
                  </CardContent>
                </SpeakerCard>
              </Grid>
            ))}
          </Grid>
        </SectionContainer>
      </Box>
    );
  };

  // Agenda section renderer
  const renderAgenda = () => {
    if (
      !sectionsData ||
      !sectionsData.agenda ||
      !sectionsData.agenda.items ||
      sectionsData.agenda.items.length === 0
    ) {
      return null;
    }

    const { agenda } = sectionsData;
    const backgroundColor = agenda.backgroundColor || "#ffffff";
    const titleColor = agenda.titleColor || "#212121";
    const textColor = agenda.textColor || "#424242";
    const accentColor = agenda.accentColor || theme.palette.primary.main;

    return (
      <Box sx={{ backgroundColor }}>
        <SectionContainer>
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography variant="h3" component="h2" sx={{ color: titleColor }}>
              {agenda.title || "Event Schedule"}
            </Typography>
            <Divider
              sx={{
                width: 50,
                mx: "auto",
                my: 2,
                borderColor: theme.palette.primary.main,
                borderWidth: 3,
              }}
            />
          </Box>

          <Box sx={{ maxWidth: 800, mx: "auto" }}>
            {agenda.items.map((session, index) => (
              <AgendaItem
                key={session.id || index}
                elevation={3}
                sx={{
                  borderLeft: `5px solid ${accentColor}`,
                  mb: 3,
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <ScheduleIcon sx={{ color: accentColor, mr: 1 }} />
                      <Typography
                        variant="body1"
                        fontWeight="bold"
                        sx={{ color: accentColor }}
                      >
                        {session.time}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{ mt: 1, color: textColor }}
                    >
                      {session.duration}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <Typography variant="h6" sx={{ color: titleColor }}>
                      {session.title}
                    </Typography>
                    {session.description && (
                      <Typography
                        variant="body2"
                        sx={{ mt: 1, color: textColor }}
                      >
                        {session.description}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </AgendaItem>
            ))}
          </Box>
        </SectionContainer>
      </Box>
    );
  };

  // Sponsors section renderer
  const renderSponsors = () => {
    if (
      !sectionsData ||
      !sectionsData.sponsors ||
      !sectionsData.sponsors.items ||
      sectionsData.sponsors.items.length === 0
    ) {
      return null;
    }

    const { sponsors } = sectionsData;
    const backgroundColor = sponsors.backgroundColor || "#f5f5f5";
    const titleColor = sponsors.titleColor || "#212121";
    const textColor = sponsors.textColor || "#424242";

    return (
      <Box sx={{ backgroundColor }}>
        <SectionContainer>
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography variant="h3" component="h2" sx={{ color: titleColor }}>
              {sponsors.title || "Our Sponsors"}
            </Typography>
            <Divider
              sx={{
                width: 50,
                mx: "auto",
                my: 2,
                borderColor: theme.palette.primary.main,
                borderWidth: 3,
              }}
            />
          </Box>

          <Grid container spacing={4} justifyContent="center">
            {sponsors.items.map((sponsor, index) => (
              <Grid item xs={6} sm={4} md={3} key={sponsor.id || index}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                  elevation={2}
                >
                  <SponsorLogo>
                    {sponsor.logo ? (
                      <img
                        src={sponsor.logo}
                        alt={sponsor.name}
                        style={{ maxWidth: "100%", maxHeight: "100%" }}
                      />
                    ) : (
                      <Typography variant="h6" sx={{ color: textColor }}>
                        {sponsor.name}
                      </Typography>
                    )}
                  </SponsorLogo>
                  <CardContent
                    sx={{
                      pt: 1,
                      flexGrow: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      align="center"
                      sx={{ color: titleColor }}
                    >
                      {sponsor.name}
                    </Typography>
                    {sponsor.website && (
                      <Button
                        variant="outlined"
                        size="small"
                        component="a"
                        href={
                          sponsor.website.startsWith("http")
                            ? sponsor.website
                            : `https://${sponsor.website}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ mt: 2 }}
                      >
                        Visit Website
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </SectionContainer>
      </Box>
    );
  };

  // Footer section renderer
  const renderFooter = () => {
    if (!sectionsData || !sectionsData.footer) return null;

    const { footer } = sectionsData;
    const backgroundColor = footer.backgroundColor || "#212121";
    const textColor = footer.textColor || "#ffffff";

    return (
      <Box sx={{ backgroundColor, py: 5 }}>
        <Container>
          <Typography variant="body1" align="center" sx={{ color: textColor }}>
            {footer.text ||
              `© ${new Date().getFullYear()} ${
                eventData?.organizedBy || "Event Organizer"
              }. All rights reserved.`}
          </Typography>
        </Container>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 8 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          variant="outlined"
          onClick={() => window.history.back()}
          sx={{ mt: 2 }}
        >
          Go Back
        </Button>
      </Container>
    );
  }

  if (!eventData || !sectionsData) {
    return (
      <Container sx={{ py: 8 }}>
        <Alert severity="warning">
          This event page is not available or has been removed.
        </Alert>
        <Button
          variant="outlined"
          onClick={() => window.history.back()}
          sx={{ mt: 2 }}
        >
          Go Back
        </Button>
      </Container>
    );
  }

  return (
    <Box>
      {renderHeader()}
      {renderAbout()}
      {renderSpeakers()}
      {renderAgenda()}
      {renderSponsors()}
      {renderFooter()}
    </Box>
  );
};

export default StandaloneLandingPagePreview;
